import type { ReactNode } from "react";
import {
  ACCENT,
  ACCENT_TEXT,
  DISPLAY,
  EDITORIAL_CONTAINER_CLS,
  EDITORIAL_SECTION_CLS,
  GROUND,
  INK,
  LABEL_CLS,
  mix,
  SECTION_INTRO_CLS,
  SECTION_TITLE_CLS,
  SURFACE_DARK,
} from "./theme";
import {
  BgNumeral,
  Grain,
  HeroMeta,
  LogoGrid,
  SectionMarker,
  type Client,
} from "./primitives";
import type {
  ClosingContent,
  Cta,
  HeroContent,
  ListItem,
  TrustContent,
} from "./types";

/* ================================================================= */
/*  Hero                                                               */
/* ================================================================= */

export function HeroSection({
  content,
  clients,
  primaryCta,
  secondaryCta,
  showEyebrow = true,
}: {
  content: HeroContent;
  clients: Client[];
  primaryCta: Cta;
  secondaryCta: Cta;
  /** Hide the top eyebrow strip when a global nav sits above the hero
   *  (e.g. the marketing home), so the two don't compete. */
  showEyebrow?: boolean;
}) {
  return (
    <section
      id="top"
      className="relative overflow-hidden"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full md:h-[40rem] md:w-[40rem]"
        style={{ backgroundColor: GROUND, opacity: 0.08 }}
      />
      <Grain opacity={0.5} />

      <div className={`relative z-10 flex min-h-[100vh] flex-col py-12 sm:py-14 md:py-20 ${EDITORIAL_CONTAINER_CLS}`}>
        {showEyebrow && (
          <div
            className="flex flex-col items-start gap-y-1 border-b pb-5 sm:flex-row sm:items-center sm:justify-between"
            style={{ borderBottomColor: mix("--theme-ground", 18) }}
          >
            <span
              style={{ color: mix("--theme-ground", 80) }}
              className={LABEL_CLS}
            >
              {content.eyebrowLeft}
            </span>
            <span
              style={{ color: mix("--theme-ground", 80) }}
              className={LABEL_CLS}
            >
              {content.eyebrowRight}
            </span>
          </div>
        )}

        <div className="flex flex-1 flex-col items-center justify-center pt-14 pb-10 text-center md:pt-20 md:pb-14">
          <h1
            style={{ ...DISPLAY, color: GROUND }}
            className="max-w-[18ch] text-[3.25rem] font-semibold leading-[0.96] tracking-[-0.035em] sm:text-[4.5rem] md:text-[6.25rem] lg:text-[7.25rem]"
          >
            {content.title}
          </h1>

          <p
            style={{ ...DISPLAY, color: mix("--theme-ground", 92) }}
            className="mt-10 max-w-[48rem] text-[1.3rem] font-medium leading-[1.4] tracking-[-0.01em] md:mt-12 md:text-[1.75rem]"
          >
            {content.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a
              href={primaryCta.href}
              className="inline-flex items-center justify-center px-6 py-3 text-[0.95rem] font-semibold tracking-[-0.005em] transition-colors"
              style={{ backgroundColor: GROUND, color: ACCENT_TEXT }}
            >
              {primaryCta.label}
            </a>
            <a
              href={secondaryCta.href}
              className="inline-flex items-center justify-center border px-6 py-3 text-[0.95rem] font-semibold tracking-[-0.005em] transition-colors"
              style={{
                borderColor: mix("--theme-ground", 38),
                color: GROUND,
              }}
            >
              {secondaryCta.label}
            </a>
          </div>
        </div>

        {clients.length > 0 ? <LogoGrid variant="dark" clients={clients} /> : null}

        <div
          className="mt-12 grid grid-cols-1 gap-y-6 border-t pt-6 sm:grid-cols-3 sm:gap-x-10"
          style={{ borderTopColor: mix("--theme-ground", 22) }}
        >
          {content.meta.map((item) => (
            <HeroMeta key={item.label} label={item.label} value={item.value} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  Paragraph (executive summary)                                     */
/* ================================================================= */

export function ParagraphSection({
  section,
  label,
  numeral,
  title,
  lead,
  body,
  pullQuote,
  pullQuoteLabel = "Why now",
}: {
  section: string;
  label: string;
  numeral: string;
  title?: string;
  lead: string;
  body: string;
  pullQuote?: string;
  pullQuoteLabel?: string;
}) {
  const isStandalone = !title && !pullQuote;

  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: GROUND, color: INK }}
    >
      <BgNumeral numeral={numeral} />
      <Grain opacity={0.45} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section={section} label={label} />

        {title && (
          <h2
            style={DISPLAY}
            className={`mt-10 max-w-[18ch] ${SECTION_TITLE_CLS}`}
          >
            {title}
          </h2>
        )}

        <div
          className={`grid max-w-5xl gap-x-10 gap-y-8 md:grid-cols-2 md:gap-x-14 ${
            title ? "mt-12 md:mt-16" : "mt-12 md:mt-14"
          }`}
        >
          <p
            style={{ ...DISPLAY, color: INK }}
            className={
              isStandalone
                ? "text-[1.25rem] font-semibold leading-[1.35] tracking-[-0.015em] sm:text-[1.4rem] md:text-[1.65rem]"
                : "text-[1.15rem] font-medium leading-[1.5] tracking-[-0.01em] md:text-[1.35rem]"
            }
          >
            {lead}
          </p>
          <p
            style={{
              ...DISPLAY,
              color: isStandalone ? INK : mix("--theme-ink", 88),
            }}
            className={
              isStandalone
                ? "text-[1.25rem] font-semibold leading-[1.35] tracking-[-0.015em] sm:text-[1.4rem] md:text-[1.65rem]"
                : "text-[1.15rem] font-medium leading-[1.5] tracking-[-0.01em] md:text-[1.35rem]"
            }
          >
            {body}
          </p>
        </div>

        {pullQuote && (
          <div
            className="mt-14 max-w-5xl border-t pt-6"
            style={{ borderColor: mix("--theme-ink", 18) }}
          >
            <p style={{ color: ACCENT_TEXT }} className={`mb-3 ${LABEL_CLS}`}>
              {pullQuoteLabel}
            </p>
            <p
              style={{ ...DISPLAY, color: mix("--theme-ink", 88) }}
              className="max-w-[60ch] text-[1.3rem] font-semibold leading-[1.35] tracking-[-0.015em] md:text-[1.55rem]"
            >
              {pullQuote}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

/* ================================================================= */
/*  Numbered list (platform / deployment / how we start)              */
/* ================================================================= */

export function ListSection({
  section,
  label,
  numeral,
  title,
  intro,
  items,
  cols = 2,
  dark = false,
  footnote,
  id,
  children,
}: {
  section: string;
  label: string;
  numeral: string;
  title: string;
  intro?: string;
  items: ListItem[];
  cols?: 2 | 3;
  dark?: boolean;
  footnote?: string;
  /** Optional anchor target for nav links. */
  id?: string;
  /** Optional exhibit content rendered after the list, before the footnote. */
  children?: ReactNode;
}) {
  const bg = dark ? SURFACE_DARK : GROUND;
  const fg = dark ? GROUND : INK;
  const titleColor = dark ? GROUND : INK;
  const introColor = dark ? mix("--theme-ground", 88) : mix("--theme-ink", 88);
  const itemNumColor = dark ? ACCENT : ACCENT_TEXT;
  const itemTitleColor = dark ? GROUND : INK;
  const itemBodyColor = dark ? mix("--theme-ground", 88) : mix("--theme-ink", 88);
  const itemExtraColor = dark ? mix("--theme-ground", 80) : mix("--theme-ink", 80);
  const borderColor = dark ? mix("--theme-ground", 22) : mix("--theme-ink", 18);
  const colClass = cols === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <section
      id={id}
      className="relative overflow-hidden scroll-mt-8"
      style={{ backgroundColor: bg, color: fg }}
    >
      <BgNumeral numeral={numeral} dark={dark} />
      <Grain opacity={dark ? 0.55 : 0.45} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section={section} label={label} dark={dark} />

        <div className="mt-10 max-w-3xl">
          <h2
            style={{ ...DISPLAY, color: titleColor }}
            className={SECTION_TITLE_CLS}
          >
            {title}
          </h2>
          {intro && (
            <p
              className={`mt-6 ${SECTION_INTRO_CLS}`}
              style={{ color: introColor }}
            >
              {intro}
            </p>
          )}
        </div>

        <ol className={`mt-14 grid gap-x-12 gap-y-10 ${colClass} md:mt-16`}>
          {items.map((item) => (
            <li
              key={item.n}
              className="grid grid-cols-[3rem_1fr] items-start gap-x-4 border-t pt-5"
              style={{ borderTopColor: borderColor }}
            >
              <span
                style={{ ...DISPLAY, color: itemNumColor }}
                className="text-[1.3rem] font-semibold leading-none tabular-nums tracking-[-0.01em]"
              >
                {item.n}
              </span>
              <div>
                <h3
                  style={{ ...DISPLAY, color: itemTitleColor }}
                  className="text-[1.15rem] font-semibold leading-[1.3] tracking-[-0.01em] md:text-[1.25rem]"
                >
                  {item.title}
                </h3>
                <p
                  className="mt-2 text-[0.98rem] leading-[1.6]"
                  style={{ color: itemBodyColor }}
                >
                  {item.body}
                </p>
                {item.extra && (
                  <p
                    className="mt-3 text-[0.88rem] leading-[1.55]"
                    style={{ color: itemExtraColor }}
                  >
                    {item.extra}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>

        {children}

        {footnote && (
          <p
            className="mt-12 max-w-3xl text-[1rem] leading-[1.6]"
            style={{ color: introColor }}
          >
            {footnote}
          </p>
        )}
      </div>
    </section>
  );
}

/* ================================================================= */
/*  Trust — company lead, named bios, client grid                     */
/* ================================================================= */

export function TrustSection({
  content,
  clients,
}: {
  content: TrustContent;
  clients: Client[];
}) {
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: GROUND, color: INK }}
    >
      <BgNumeral numeral={content.numeral} />
      <Grain opacity={0.45} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section={content.section} label={content.label} />

        <h2
          style={DISPLAY}
          className={`mt-10 max-w-[20ch] ${SECTION_TITLE_CLS}`}
        >
          {content.title}
        </h2>

        <p
          style={{ ...DISPLAY, color: INK }}
          className="mt-12 max-w-3xl text-[1.25rem] font-semibold leading-[1.35] tracking-[-0.015em] sm:text-[1.4rem] md:mt-14 md:text-[1.65rem]"
        >
          {content.lead}
        </p>

        <div className="mt-14 grid max-w-5xl gap-x-10 gap-y-12 md:mt-16 md:grid-cols-2 md:gap-x-14">
          {content.team.map((member) => (
            <div
              key={member.name}
              className="border-t pt-6"
              style={{ borderTopColor: mix("--theme-ink", 18) }}
            >
              <p style={{ color: ACCENT_TEXT }} className={`mb-2 ${LABEL_CLS}`}>
                {member.role}
              </p>
              <h3
                style={{ ...DISPLAY, color: INK }}
                className="text-[1.35rem] font-semibold leading-[1.2] tracking-[-0.015em] md:text-[1.6rem]"
              >
                {member.name}
              </h3>
              <p
                className="mt-4 text-[1.02rem] leading-[1.6]"
                style={{ color: mix("--theme-ink", 88) }}
              >
                {member.bio}
              </p>
            </div>
          ))}
        </div>

        {clients.length > 0 ? (
          <div className="mt-16 md:mt-20">
            <LogoGrid variant="light" clients={clients} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ================================================================= */
/*  Closing                                                            */
/* ================================================================= */

export function ClosingSection({ content }: { content: ClosingContent }) {
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-24 z-0 h-[26rem] w-[26rem] rounded-full md:h-[36rem] md:w-[36rem]"
        style={{ backgroundColor: ACCENT, opacity: 0.1 }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-16 -bottom-16 z-0 h-48 w-48 md:h-64 md:w-64"
        style={{ backgroundColor: "#1B1913", opacity: 0.4 }}
      />
      <Grain opacity={0.5} />

      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section={content.section} label={content.label} dark />

        <h2
          style={{ ...DISPLAY, color: GROUND }}
          className="mt-10 max-w-[24ch] text-[2.6rem] font-semibold leading-[1.04] tracking-[-0.03em] sm:text-[3.5rem] md:text-[5rem] lg:text-[5.5rem]"
        >
          {content.title}
        </h2>
        <p
          style={{ ...DISPLAY, color: mix("--theme-ground", 88) }}
          className="mt-10 max-w-[52ch] text-[1.25rem] leading-[1.45] tracking-[-0.005em] md:text-[1.5rem]"
        >
          {content.body}
        </p>

        <div className="mt-12 flex flex-wrap gap-4 md:mt-16">
          <a
            href={content.primaryCta.href}
            className="inline-flex items-center justify-center px-7 py-4 text-[0.95rem] tracking-[-0.005em] transition-colors"
            style={{ backgroundColor: GROUND, color: ACCENT_TEXT }}
          >
            {content.primaryCta.label}
          </a>
          <a
            href={content.secondaryCta.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border px-7 py-4 text-[0.95rem] tracking-[-0.005em] transition-colors"
            style={{
              borderColor: mix("--theme-ground", 40),
              color: GROUND,
            }}
          >
            {content.secondaryCta.label}
          </a>
        </div>

        <div
          className="mt-20 grid grid-cols-1 gap-y-6 border-t pt-8 sm:grid-cols-3 sm:gap-x-10"
          style={{ borderTopColor: mix("--theme-ground", 22) }}
        >
          {content.meta.map((item) => (
            <HeroMeta key={item.label} label={item.label} value={item.value} />
          ))}
        </div>
      </div>
    </section>
  );
}
